/**
 * Leasing Showings — boundary contract schemas.
 *
 * Owned here, not beside the route: `apps/api/src/routes/manager/leasing-showings.schemas.ts`
 * imports and re-exports these to register the routes, and
 * `apps/landlord/src/lib/leasing.api.ts` derives its `ShowingAttendee` / `ShowingAppointment`
 * / `ScheduleAppointmentParams` / `UpdateAppointmentParams` types from the same objects.
 * See packages/api-types/README.md.
 *
 * Covers every route returning a ShowingAppointment (as `appointment`, or within `items`) or
 * a ShowingAttendee (as `attendee`, or within a ShowingAppointment's `attendees`):
 *   GET   /landlord/api/listings/:id/showings
 *   POST  /landlord/api/listings/:id/showings
 *   PATCH /landlord/api/listings/:id/showings/:appointmentId
 *   POST  /landlord/api/listings/:id/showings/:appointmentId/attendees
 *   PATCH /landlord/api/listings/:id/showings/:appointmentId/attendees/:attendeeId
 */
import { z } from 'zod';
const uuid = z.uuid();
const dateString = z.string(); // ISO 8601 string
// Enums — must match ShowingKind, ShowingOutcome, RsvpStatus in @resida/database.
export const ShowingKindSchema = z.enum(['private', 'open_house']);
export const ShowingOutcomeSchema = z.enum(['scheduled', 'completed', 'cancelled', 'rescheduled']);
export const RsvpStatusSchema = z.enum(['invited', 'confirmed', 'declined', 'attended', 'no_show']);
// ---------------------------------------------------------------------------
// ShowingAttendee response
// ---------------------------------------------------------------------------
export const ShowingAttendeeResponseSchema = z.object({
    id: uuid,
    showingAppointmentId: uuid,
    leadId: uuid,
    rsvpStatus: RsvpStatusSchema,
    notes: z.string().nullable(),
    createdAt: dateString,
    updatedAt: dateString,
});
// ---------------------------------------------------------------------------
// ShowingAppointment response
// ---------------------------------------------------------------------------
export const ShowingAppointmentResponseSchema = z.object({
    id: uuid,
    listingId: uuid,
    landlordScopeId: uuid,
    kind: ShowingKindSchema,
    scheduledFor: dateString,
    durationMinutes: z.number().int().positive(),
    location: z.string().nullable(),
    notes: z.string().nullable(),
    maxAttendees: z.number().int().positive().nullable(),
    outcome: ShowingOutcomeSchema,
    createdByUserId: uuid,
    createdAt: dateString,
    updatedAt: dateString,
    metadata: z.record(z.string(), z.unknown()),
    attendees: z.array(ShowingAttendeeResponseSchema),
});
// ---------------------------------------------------------------------------
// Schedule appointment — POST /landlord/api/listings/:id/showings (request body)
// ---------------------------------------------------------------------------
export const ScheduleAppointmentBodySchema = z.object({
    kind: ShowingKindSchema,
    scheduledFor: z.iso.datetime(),
    durationMinutes: z.number().int().positive().optional(),
    location: z.string().max(500).nullable().optional(),
    notes: z.string().nullable().optional(),
    maxAttendees: z.number().int().positive().nullable().optional(),
    /** Required when kind = 'private' */
    leadId: uuid.optional(),
});
// ---------------------------------------------------------------------------
// Update appointment — PATCH /landlord/api/listings/:id/showings/:appointmentId (request body)
// ---------------------------------------------------------------------------
export const UpdateAppointmentBodySchema = z.object({
    scheduledFor: z.iso.datetime().optional(),
    durationMinutes: z.number().int().positive().optional(),
    location: z.string().max(500).nullable().optional(),
    notes: z.string().nullable().optional(),
    outcome: ShowingOutcomeSchema.optional(),
});
//# sourceMappingURL=showings.js.map